/**
 * Changelog / Historial de Versiones
 *
 * Mantener actualizado con cada cambio significativo.
 * Seguir formato Semantic Versioning: MAJOR.MINOR.PATCH
 *
 * - MAJOR: Cambios incompatibles con versiones anteriores
 * - MINOR: Nueva funcionalidad compatible hacia atrás
 * - PATCH: Correcciones de bugs compatibles hacia atrás
 */

export const APP_VERSION = "1.28.0";

export const changelog = [
  {
    version: "1.28.0",
    date: "2026-04-27",
    title: "Administration module: full RBAC admin + i18n + structural refactor",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "New Access Control section with full RBAC admin (Roles, Modules, Permissions, Role Permissions Matrix)",
        details: "Wires the 17 RbacAdmin endpoints (/api/RbacAdmin) into the UI. New tabs: RolesCatalogTab (CRUD + cascade-aware deactivate/reactivate, blocks system roles), ModulesCatalogTab (CRUD + cascade-aware deactivate/reactivate), PermissionsCatalogTab (create/update only — backend keeps permissions immutable), RolePermissionsMatrixTab (the killer feature: select role → grid module×permission with checkboxes → bulk save with insert/reactivate/revoke summary). New service src/utils/services/control/rbacAdminService.js with all 17 functions documented. The previous read-only RolePermissionsView is replaced by the editable matrix."
      },
      {
        category: "refactor",
        description: "Administration sidebar reorganized for clarity",
        details: "Two top-level groups for non-technical admins: PEOPLE (Employees, Locations) and ACCESS CONTROL (User Roles, Roles, Modules, Permissions, Role Permissions). PCP Configuration removed from Administration entirely — will move to the Assignments module in a follow-up. Orphaned UsersTab is removed; user account management (deactivate, unlock, reset password, force logout, MFA) is now consolidated inside EmployeeDetailDialog as a single source of truth."
      },
      {
        category: "improvement",
        description: "Employees list shows all rows with internal scroll (no more pagination)",
        details: "EmployeesDataGrid now uses a fixed-height container with hidden footer pagination. The admin sees the entire roster at once and scrolls inside the grid. Sort and filters still work as before."
      },
      {
        category: "improvement",
        description: "EmployeeDetailDialog renders all assigned roles instead of just the first",
        details: "Backend's getEmployeeInfo returns one row per role; the dialog previously read only info[0].roleName, hiding additional roles for users with multiple assignments. Now renders a chip list with every active role."
      },
      {
        category: "improvement",
        description: "MFA management consolidated into employee detail",
        details: "MfaManageDialog moved from the (deleted) UsersTab to the EmployeeDetailDialog action row, available for any employee with an active user account. The dialog reads mfaEnabled / mfaFrequencyDays from the freshly loaded employee info so the state is always current when opened."
      },
      {
        category: "feature",
        description: "Full i18n coverage for the administration module (es / en / cr)",
        details: "Every label, helper text, snackbar message, confirm dialog message, and column header in the administration module now goes through useTranslation. New admin.* namespace added to src/locales/es.json, en.json, and cr.json with ~250 new keys covering employees, locations, user roles, RBAC catalogs, and the role-permissions matrix."
      },
      {
        category: "improvement",
        description: "Real /api/RbacAdmin/roles endpoint replaces the hardcoded roles placeholder",
        details: "masterDataService.getRoles() previously returned a static [Admin, PCP, Care Management, CEO] list because the SP was not implemented. It now lazy-imports rbacAdminService.listRoles(statusFilter=1) and adapts the response to the legacy {id, name, key} shape used by CreateUserDialog and UserRolesTab so existing consumers keep working without changes."
      },
      {
        category: "refactor",
        description: "Shared admin hooks remove duplication across tabs",
        details: "New hooks under src/modules/administration/hooks/: useSnack (encapsulates the {open, message, severity, showSnack, closeSnack} pattern repeated in 4 tabs), useResourceCrud (the try/catch + res.result!==0 pattern repeated for every CRUD operation), useUserAccountActions (the deactivate/reactivate/unlock/forceLogout/resetPassword switch repeated between UsersTab and EmployeeDetailDialog). New shared component CatalogFormDialog factors out the name+key+description form used by Roles, Modules and Permissions catalogs."
      },
      {
        category: "deprecated",
        description: "Removed obsolete administration components",
        details: "Deleted src/modules/administration/components/users/ (UsersTab, UsersDataGrid, CreateUserDialog moved to employees/, MfaManageDialog moved to employees/), src/modules/administration/components/pcp-config/ (PCP work moves to Assignments module), src/modules/administration/components/employees/PcpConfigPanel.jsx (PCP no longer embedded in employee detail), src/modules/administration/components/roles/ (UserRolesManager, RolesTab, RolePermissionsView superseded by access-control/ tabs)."
      }
    ]
  },
  {
    version: "1.27.0",
    date: "2026-04-23",
    title: "Coding Opportunities: dead code cleanup + documentation restructure",
    type: "minor",
    changes: [
      {
        category: "refactor",
        description: "Removed dead AI pipeline code from the front",
        details: "After migrating to the backend AI proxy (1.26.x), the following files are no longer imported from anywhere and were deleted: src/modules/coding-opportunities/IAsystem/pipelines/ (runNotesExtractor, runLabsExtractor, runCodingAssessment), IAsystem/prompts/ (4 files), IAsystem/clients/openaiClient.js (local re-export), IAsystem/utils/ (safeJson, chunking, telemetry), IAsystem/ui/ (6 components of an alternative experimental UI that was never mounted in prod), IAsystem/index.js, components/Worklist/PatientDetailDrawer.jsx (orphan), and the in-module workreadme/ folder. Git history preserves the original files. IAsystem/mappers/ (notes/labs/codingOps flatten) stays because active components still use them to build the deterministic text sent to the backend."
      },
      {
        category: "docs",
        description: "Restructured docs/MCMOS/CodingOpportunities",
        details: "Updated 00_INDEX.md with current status markers. 01_MODULE_OVERVIEW.md reflects the post-cleanup folder structure and dependency notes. 03_API_SERVICES.md adds the AI endpoints section and a pending-asks list. 05_UI_FLOW.md diagram updated with the backend proxy + labs chain flow. 06_AI_PIPELINE.md fully rewritten to describe the current state (backend proxy, 3-source evidence, labs chain, adapters, rate limits). 07_REFACTOR_NOTES.md slimmed to reflect what's done (migration + cleanup) and absorbed the backend asks (sourceDecision, TAB_AI_CALLS) from the former doc 09. Deleted AI-source-snapshot/ folder (no longer needed for analysis)."
      }
    ]
  },
  {
    version: "1.26.2",
    date: "2026-04-23",
    title: "Labs analysis chained before coding-assess (full 3-source evidence)",
    type: "minor",
    changes: [
      {
        category: "improvement",
        description: "Coding-assess now sees labs as the 3rd evidence source",
        details: "When the professional clicks 'Analyze X Coding Opportunities', CodingOppsAnalysisStep first calls /ai/labs-extract on the patient's AdvancedMD labs (if any) and then passes that labsSummary into /ai/coding-assess. The AI prompt already considers notes (key_findings + medications) + labs (abnormal_labs) + coding_ops as three parallel evidence streams (08_AI_BACKEND_ENDPOINTS.md §4). This closes the clinical gap where conditions like diabetic nephropathy (E11.22 / HCC 18) or CKD stage 3 (N18.3x / HCC 138) were previously unreachable unless the provider explicitly wrote them in the note — now the AI can infer them from eGFR/creatinine patterns combined with diabetes findings and ACEi dose-adjusted medications."
      },
      {
        category: "improvement",
        description: "Labs extraction is cached per patient session and degrades gracefully",
        details: "Local state `labsAnalysis` in CodingOppsAnalysisStep caches the /ai/labs-extract response so re-clicking 'Analyze' doesn't re-spend the rate limit. Cache drops on unmount (new patient → new labs). If /ai/labs-extract fails (429, backend error, malformed JSON), the flow logs a console.warn and proceeds with labsSummary: null instead of aborting — preferable to a hard block for the professional."
      },
      {
        category: "docs",
        description: "Doc 09 (AI adjustments) gained §4 describing the labs flow",
        details: "Added Cambio 4 to docs/MCMOS/CodingOpportunities/09_AI_ADJUSTMENTS_AND_CHANGES.md covering clinical motivation, design decision (automatic chain vs separate button), execution rules, failure handling, updated end-to-end diagram, files touched, rate-limit/cost considerations, and non-blocking future evolution options."
      }
    ]
  },
  {
    version: "1.26.1",
    date: "2026-04-23",
    title: "Medications flow re-enabled end to end (AI notes → coding assess)",
    type: "minor",
    changes: [
      {
        category: "improvement",
        description: "Medications extracted from clinical notes now flow into the coding-assess decision",
        details: "Backend's /ai/notes-extract now returns a medications[] array (medication, active, treats, icd10_hint, source_note_id, date, confidence) and /ai/coding-assess uses them as a 3rd evidence source alongside notes and labs (08_AI_BACKEND_ENDPOINTS.md §2.2, §4). On the front, adaptNotesExtractResponse in ClinicalNotesTab.jsx and the equivalent block in NotesAnalysisStep.jsx now map that array; buildNotesSummaryForBackend in CodingOppsAnalysisStep.jsx propagates it inside notesSummary when calling /coding-assess. IAAssistTab's combiner and the 'View all medications' dialog re-populate automatically because they already read analysis.medications per note."
      },
      {
        category: "improvement",
        description: "Coding-assess supporting_evidence now supports type='medication'",
        details: "The coding-assess response can attach a supporting_evidence entry with type 'medication' (e.g. 'Metformin 850mg BID supports E11.9'). The adapter adaptCodingAssessResponse in CodingOppsAnalysisStep.jsx already handles unknown types gracefully — no UI change needed to render it."
      }
    ]
  },
  {
    version: "1.26.0",
    date: "2026-04-23",
    title: "Coding Opportunities AI moved to backend proxy",
    type: "minor",
    changes: [
      {
        category: "refactor",
        description: "AI calls for Coding Opportunities now proxy through the backend",
        details: "Direct browser-side calls to OpenAI (callOpenAIChat) were replaced by POST to /api/CodingOpportunities/ai/notes-extract and /api/CodingOpportunities/ai/coding-assess. New thin client at src/utils/services/codingOpportunitiesAiClient.js with 3 functions (aiNotesExtract, aiLabsExtract, aiCodingAssess), patient whitelist, and 429 rate-limit handling that surfaces Retry-After. Touched ClinicalNotesTab.jsx (2 sites), IAAssist/NotesAnalysisStep.jsx (2 sites) and IAAssist/CodingOppsAnalysisStep.jsx (1 site). UI/UX unchanged — all swaps are internal; dead prompt builders removed."
      },
      {
        category: "improvement",
        description: "Response adapters keep the existing UI contract",
        details: "Backend returns {summary, key_findings, coding_relevance} for notes and {patient_summary, opportunities[]} for coding-assess. Small adapter layer maps key_findings→findings (per-note), wraps as {notesSummary: response} (all notes), and for coding maps patient_summary→overall_summary plus diagnosisCode→code and hccDescription→description, so no JSX/MUI components had to change."
      },
      {
        category: "security",
        description: "OpenAI API key no longer needed in the browser for Coding Opportunities flow",
        details: "With the proxy in place, REACT_APP_OPENAI_API_KEY is only still used by clinical-notes/AiCodingClinicalNotePage.jsx (uploadFileToOpenAI / callOpenAIResponses), which is out of scope for this refactor. Once that page is migrated, the key can be removed from .env entirely."
      }
    ]
  },
  {
    version: "1.25.1",
    date: "2026-04-23",
    title: "High Risk migrated to /api/Census/high-risk",
    type: "minor",
    changes: [
      {
        category: "refactor",
        description: "HighRisk now consumes the new /api/Census/high-risk endpoint",
        details: "Replaced legacy /api/PatientInfo/high-risk with /api/Census/high-risk. Service accepts a filters object (cipId, locationId, searchName, dateOfBirth, onlyActive, ppvEr, lace, bhStatus) and only forwards defined values as query params. JSDoc added with full request/response contract. Role scope is enforced server-side."
      },
      {
        category: "feature",
        description: "New filters in HighRisk table",
        details: "Search by name, CIP ID, DOB, location, ternary Yes/No/All filters for PPV-ER, LACE and BH Status, plus 'Include inactive' switch to see historical records. Insurance-type filter (ACA/MED) removed — the new Census endpoint does not accept it."
      },
      {
        category: "improvement",
        description: "Richer HighRisk columns sourced directly from the Census response",
        details: "Each row now shows cipId, fullName, DOB, gender, phone, mobile, email, flagsActive (0-3 chip by criticality), individual PPV-ER / LACE / BH chips, firstSeen, lastSeen, status (Active/Inactive badge), medical center and address. Inactive rows rendered in a greyed-out style."
      },
      {
        category: "refactor",
        description: "HighRiskContext uses unified filters object + updateFilter / resetFilters",
        details: "Consistent with Discharged, Admitted and Emergency contexts. Default onlyActive=true so the toggle has visible effect."
      }
    ]
  },
  {
    version: "1.25.0",
    date: "2026-04-23",
    title: "Census endpoints migration for Discharged / Admitted / Emergency",
    type: "minor",
    changes: [
      {
        category: "refactor",
        description: "Discharged, Admitted and Emergency now consume the new /api/Census/* endpoints",
        details: "Replaced legacy /api/PatientInfo/{discharged|admitted|emergency} with /api/Census/{discharged|admitted|emergency}. Services now accept a filters object and only forward defined values as query params. JSDoc added per CLAUDE.md convention with full request/response contract. Role scope is enforced server-side — the front no longer filters by employee location; a user-selected locationId still filters inside the allowed scope."
      },
      {
        category: "feature",
        description: "New filters in Discharged / Admitted / Emergency tables",
        details: "Each table now exposes: search by name, CIP ID, DOB, location, date from/to, min LACE+. Discharged adds 'Only overdue' switch and Follow-up status select. Admitted adds 'Min admit days'. Emergency adds 'Facility name' LIKE filter. Insurance-type filter (ACA/MED) removed — the new Census endpoints do not accept it."
      },
      {
        category: "improvement",
        description: "Richer columns sourced directly from the Census response",
        details: "Each row now shows cipId, fullName, DOB, gender, phone, mobile, email, admit/discharge dates, LACE+ score (chip-coded by risk), diagnoses (ICD list), facility, place of service, attending physician. Discharged highlights overdue rows in red and shows follow-up status, due date, call count, last contact. The front no longer makes a secondary PatientInfo call — all data comes in the first response."
      },
      {
        category: "refactor",
        description: "Context providers now expose a unified filters object + updateFilter / resetFilters",
        details: "DischargedContext, AdmittedContext and EmergencyContext replaced individual setters (setLocationId, setMemberType) with a filters state + updateFilter(key, value) + resetFilters helpers. The effect re-fetches whenever any filter changes."
      }
    ]
  },
  {
    version: "1.24.2",
    date: "2026-04-17",
    title: "Fix: users stuck on Change Password page after success",
    type: "patch",
    changes: [
      {
        category: "fix",
        description: "Await signOut before navigating to /Login after a successful password change",
        details: "After a forced password change, employees were bouncing back to /auth/change-password instead of reaching /Login. Root cause: ChangePasswordPage called signOut() (which is async — it awaits logoutV2() on the backend before clearing user state and localStorage token) without awaiting it, and then called navigate('/Login') immediately. By the time LoginPage mounted, user and token were still in state with forcePasswordChange=true, so its useEffect at LoginPage.jsx:62-74 redirected the user straight back to the change-password flow. Fix awaits signOut() inside the setTimeout before navigate, so the session is fully cleared before LoginPage evaluates whether to redirect."
      }
    ]
  },
  {
    version: "1.24.1",
    date: "2026-04-17",
    title: "Dashboards refreshed with April 17 data",
    type: "patch",
    changes: [
      {
        category: "improvement",
        description: "CEO, Care Manager and Clinical dashboards updated with 2026-04-17 snapshot",
        details: "Data for the three main dashboards was regenerated from the April 17 export at src/pages/Main/Temp/AprilData/aprl17/. CEO KPIs (population, cost, quality, centers, trends, coding, engagement, performance) now come from DataDashCEO_v5. Care Manager and Clinical folders were refreshed for all 11 locations (fort_myers, fort_pierce, hialeah, immokalee, lake_worth, lehigh_acres, naples, plantation, port_st_lucie, stuart). sourceDate on every file reads 2026-04-17. No changes to file paths, schemas or UI components — only payload content."
      },
      {
        category: "config",
        description: "Added scripts/transform-april17-data.js",
        details: "New import script mirrors the pattern of transform-april-data.js but points at AprilData/aprl17/ and DataDashCEO_v5. Run with `node scripts/transform-april17-data.js` to reproduce the refresh. Keeps prior import scripts untouched for traceability."
      }
    ]
  },
  {
    version: "1.24.0",
    date: "2026-04-17",
    title: "Reactivate Employee in Administration",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Reactivate a previously deactivated employee from the Employees tab",
        details: "The Employees grid now shows a green 'Reactivate' action on rows whose status is 0 (mirroring the existing Deactivate action on active rows). The action calls PUT /api/MasterData/employees/reactivate with { employeeId } — the endpoint flips NUM_STATUS back to 1 on TAB_EMPLOYEES only. Reactivation is NOT cascading by design: the user account, roles, locations and PCP config that were deactivated along with the employee are NOT restored automatically, to avoid reviving permissions that may no longer apply to the employee's current role. The confirmation dialog warns the admin to reassign those manually."
      },
      {
        category: "feature",
        description: "reactivateEmployee service wrapper in masterDataService",
        details: "New exported function reactivateEmployee(employeeId) in src/utils/services/control/masterDataService.js with JSDoc documenting the endpoint contract, non-cascade behavior and error codes (result=1 → 'Employee not found or already active')."
      }
    ]
  },
  {
    version: "1.23.1",
    date: "2026-04-16",
    title: "Primary Location management in Administration",
    type: "patch",
    changes: [
      {
        category: "feature",
        description: "Set-as-primary action in employee locations table",
        details: "EmployeeDetailDialog now renders the Primary column as an interactive star: solid on the current primary, outlined on secondaries. Clicking the outlined star calls POST /api/MasterData/employee-locations/assign twice — first with primary:true on the new target to promote it, then with primary:false on the previous primary to demote it. The promote-first order matters because the backend refuses to leave an employee without an active primary, so the demote call only succeeds once another primary already exists."
      },
      {
        category: "improvement",
        description: "First assigned location defaults to primary",
        details: "When an employee has no locations yet, the first Assign call now sends primary:true so AuthContext.primaryLocation (which filters by isPrimary) always resolves without falling back to locations[0]."
      },
      {
        category: "improvement",
        description: "Block delete on the primary location",
        details: "The Remove action in the locations table is disabled for the row marked as primary. The admin must promote another location first — prevents leaving the employee with all rows at isPrimary=false."
      },
      {
        category: "improvement",
        description: "Sort employee locations alphabetically in the admin dialog",
        details: "The backend SP returns rows with primary first (BIT_PRIMARY DESC), so flipping the primary made the promoted row jump to the top and looked like 'nothing happened'. EmployeeDetailDialog now sorts client-side by medicalCenter (localeCompare, case-insensitive), so rows stay in the same visual position when the star toggles."
      },
      {
        category: "improvement",
        description: "Admin data grids: larger page sizes and sortable Name column",
        details: "Employees, Users, Locations and PCP Config grids now expose pageSizeOptions=[25, 50, 100] with a default of 100 — the MIT DataGrid caps pageSize at 100, so going above requires DataGridPro. The Name column in Employees and Users grids now has a valueGetter that concatenates firstName+lastName — DataGrid can finally sort and filter by full name. Employees, Users and Locations grids also land with a default alphabetical sort."
      }
    ]
  },
  {
    version: "1.23.0",
    date: "2026-04-16",
    title: "Mandatory 2FA Enrollment Gate (HIPAA)",
    type: "minor",
    changes: [
      {
        category: "security",
        description: "Block app access for users who haven't enrolled 2FA",
        details: "Per HIPAA Security Rule 45 CFR §164.312(d) — Person or Entity Authentication — every user accessing PHI must authenticate with at least two factors. A new /mfa-required gate page now intercepts any authenticated user whose mfaEnabled=false and blocks navigation to the rest of the application until they complete TOTP enrollment (QR scan → 6-digit code → save 8 recovery codes). ProtectedRoute enforces the redirect globally."
      },
      {
        category: "fix",
        description: "User profile showed 2FA status as Inactive even when active",
        details: "Fixed the bug where MfaSection always rendered 'Inactive' because /api/MasterData/employee-info/{id} didn't include the mfaEnabled field. The backend now returns mfaEnabled (bool) for every employee row; the frontend already reads it via AuthContext.runPostLoginFlow, so the real status now reaches MfaSection, UsersDataGrid and the new /mfa-required gate."
      },
      {
        category: "fix",
        description: "Refresh cached session against backend on app load",
        details: "Users who had an active session from before the mfaEnabled field existed were being incorrectly sent to the /mfa-required gate because their cached user object didn't carry the flag. AuthContext now re-fetches getEmployeeInfo on app startup whenever a cached session is restored and merges the fresh mfaEnabled / profile fields into the stored user before routing decisions run."
      },
      {
        category: "docs",
        description: "Document mfaEnabled field in getEmployeeInfo JSDoc",
        details: "Updated src/utils/services/control/masterDataService.js to reflect the new response shape returned by /api/MasterData/employee-info/{id}, including how the frontend uses mfaEnabled to enforce the HIPAA 2FA gate."
      }
    ]
  },
  {
    version: "1.22.0",
    date: "2026-04-15",
    title: "Two-Factor Authentication (TOTP) + Idle Timeout Fix",
    type: "minor",
    changes: [
      {
        category: "security",
        description: "Two-Factor Authentication (2FA) with TOTP standard",
        details: "Full 2FA flow using TOTP RFC 6238 (compatible with Microsoft Authenticator, Google Authenticator, Authy, 1Password, Bitwarden). Includes enrollment from profile (scan QR + 8 recovery codes), login verification with 2-minute pre-auth token, recovery code fallback when authenticator is unavailable, and admin controls to disable 2FA or configure per-user verification frequency (1-365 days)."
      },
      {
        category: "feature",
        description: "MFA enrollment UI in User Profile",
        details: "New MfaSection component with 3-step enrollment (start → scan QR with qrcode.react → save recovery codes), non-closable modal showing the 8 recovery codes once with Download/Copy/Print buttons, and disable dialog with confirmation. Fully i18n (EN/ES/CR)."
      },
      {
        category: "feature",
        description: "MFA verify and recovery steps in LoginPage",
        details: "LoginPage now detects mfaRequired from /api/AuthV2/login and switches to internal mfa-verify step (6-digit input with 2-min countdown timer) or mfa-recovery step (xxxx-xxxx-xxxx code). Pre-auth token lives only in memory (AuthContext.preAuth), never persisted."
      },
      {
        category: "feature",
        description: "Admin MFA controls in Administration module",
        details: "New MfaManageDialog in UsersTab: view user 2FA status, configure verification frequency (with recommended values per role), and disable 2FA for users who lost access. UsersDataGrid now shows a 2FA status column with frequency chip."
      },
      {
        category: "feature",
        description: "Persistent low-codes banner in Layout",
        details: "When a user signs in via recovery code and only has 2 or fewer remaining, a persistent banner appears in the Layout (above all routes) prompting them to regenerate 2FA. Banner auto-clears after re-enrollment."
      },
      {
        category: "fix",
        description: "Idle session timeout not firing in production",
        details: "Fixed critical bug in useSessionTimeout hook where isWarningVisible in useEffect deps caused the effect to re-run and reset the timer the moment the warning modal appeared, so users stayed logged in indefinitely. Now uses a ref for isWarningVisible to keep the listener closure current without re-running the effect. Default timeout raised to 30 minutes (configurable via REACT_APP_SESSION_TIMEOUT) with 60s warning window."
      },
      {
        category: "security",
        description: "HIPAA automatic logoff now actually works",
        details: "45 CFR 164.312(a)(2)(iii) compliance restored. After 30 minutes of inactivity, the session warning modal shows a 60-second countdown; if the user does not respond, signOut() is invoked and they are redirected to /Login with an expiration message."
      },
      {
        category: "refactor",
        description: "Removed legacy Login and ChangePassword pages",
        details: "Deleted src/pages/Login/ (including LoginComp/) and src/pages/ChangePassword/ — both unused since the migration to src/modules/login-v2/. AppRouter comments cleaned up."
      },
      {
        category: "config",
        description: "New mfaService.js with full JSDoc for 6 MFA endpoints",
        details: "src/utils/services/control/mfaService.js documents /api/Mfa/start-enrollment, /api/Mfa/confirm-enrollment, /api/Mfa/verify, /api/Mfa/recovery, /api/Mfa/disable, /api/Mfa/frequency with request/response examples and all error result codes following the project's API documentation convention."
      }
    ]
  },
  {
    version: "1.20.0",
    date: "2026-03-24",
    title: "BH Dashboard + Agent System + Specialist Referrals Services + Architecture Docs",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Behavioral Health Dashboard with 7 panels",
        details: "New BHDashboard with Overview, Risk, Substance Use, ER Utilization, Unmanaged Patients, Care Programs, and Outreach panels. Location-based data filtering with mock JSON data per center."
      },
      {
        category: "feature",
        description: "Agent system for AI-assisted development and auditing",
        details: "7 specialized agents (Arqui, Bocki, Fransis, Clift, Astheron, Rafiky, Norman) with role definitions, daily tasks, and work reports. First full-system audit completed identifying 7 critical, 14 high, and 10 medium findings."
      },
      {
        category: "feature",
        description: "Specialist Referrals backend services and SP documentation",
        details: "Added specialistReferralsService.js and labPhlebotomyService.js with real API integration. Includes SP documentation for Groups 1-5 covering CRUD operations, comments, and catalog endpoints."
      },
      {
        category: "refactor",
        description: "Lab Phlebotomy migrated from pages/ to modules/ pattern",
        details: "Moved LabPhlebotomy from src/pages/Tools/Apps/LabPhlebotomy/ to src/modules/lab-phlebotomy/ following the self-contained module architecture. Old location deleted."
      },
      {
        category: "improvement",
        description: "CEO Dashboard v2 data structure and Care Manager dashboard data updates",
        details: "New DataDashCEO_v2/ with granular JSON files for cost, coding, quality, performance, population, engagement, and trends. Updated CareManager and Clinical dashboard data for all 11 locations."
      },
      {
        category: "docs",
        description: "ARCHITECTURE.md, TEAM.md, and presentation documentation",
        details: "Master architecture document consolidating system overview, stack, 3-tier architecture, 15 modules, 6 dashboards, 5 DBs, AI Gateway, RBAC, and Constitution rules. Team documentation for 7 agents. 19 presentation slides and 15 module docs."
      },
      {
        category: "refactor",
        description: "Removed legacy service files and old LabPhlebotomy location",
        details: "Deleted 13 legacy getPatientInfo.js files from src/utils/services/APP/ and old LabPhlebotomy pages. Cleaned up unused service patterns."
      }
    ]
  },
  {
    version: "1.19.0",
    date: "2026-02-19",
    title: "Employee Documents — Document Management Module",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "New Employee Documents module for managing work-related files",
        details: "Self-contained module at /employee-documents with file selection (metadata only — no file content stored), DataGrid table with search and category filters, detail dialog for editing category/description, and delete functionality. Data persists in localStorage."
      },
      {
        category: "feature",
        description: "Drag & drop upload area with multi-file support",
        details: "Visual drop zone with dashed border and CloudUpload icon. Supports click-to-browse and drag-and-drop. Extracts only file metadata (name, size, type) from File objects — never reads file content via FileReader."
      },
      {
        category: "feature",
        description: "Universal access for all authenticated roles",
        details: "Module added to UNIVERSAL_ROUTES and ROUTE_PERMISSIONS with null (no specific permission required). DOCUMENTS_READ and DOCUMENTS_WRITE permissions added to PCP and Care Management roles. Accessible from Administration sidebar section."
      },
    ],
  },
  {
    version: "1.18.0",
    date: "2026-02-17",
    title: "CEO Executive Dashboard — 7 Panels with Real Data",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Complete CEO Executive Dashboard with 7 data-driven panels",
        details: "Replaced mock-data CEO dashboard with full implementation consuming real consolidated JSON data (37 KB across 7 files). Includes: Panel 1 (Population Overview with health distribution bar), Panel 2 (Cost Analysis with Nivo donut chart + expandable PCP/facility tables), Panel 3 (Quality Measures with HEDIS progress bars + targets), Panel 4 (Center Performance heatmap table with alerts), Panel 5 (Trends line chart + Top Performers medals), Panel 6 (Coding Revenue with HCC recapture rates), Panel 7 (Engagement & Growth with visit breakdown + contact availability)."
      },
      {
        category: "feature",
        description: "Consolidated data service for executive view",
        details: "New ceoDataService loads 7 JSON datasets in parallel (no location filter — CEO sees all centers). useCEOData hook provides individual loading states per panel. Help content (InfoButton) added to every panel header."
      },
      {
        category: "improvement",
        description: "Interactive charts and sortable tables for executive analysis",
        details: "Nivo PieChart for cost breakdown, Nivo LineChart for care gap trends (last 18 months), sortable Center Performance table with color-coded heatmap, expandable tables with toggle buttons, HEDIS compliance bars with target markers."
      },
    ],
  },
  {
    version: "1.17.0",
    date: "2026-02-17",
    title: "Care Manager Dashboard — 7 Panels + Task Integration",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Complete Care Manager Dashboard with 7 specialized panels",
        details: "Replaced mock data CareManager dashboard with full implementation consuming real JSON data from 11 locations. Includes: Panel 1 (KPI Overview with 4 clickable cards), Panel 2 (Transition of Care with LACE scores), Panel 3 (Unmanaged High Risk sorted by severity), Panel 4 (ER & Utilization with PPV-ER risk), Panel 5 (Outreach with contact info links), Panel 6 (My Tasks with full CRUD), Panel 7 (Polypharmacy Alerts with lab status)."
      },
      {
        category: "feature",
        description: "Location-aware data loading for Care Management",
        details: "New careManagerDataService loads 6 JSON datasets per location in parallel. useCareManagerData hook provides individual loading states per panel. LocationSelector reused from Clinical dashboard with CM-specific data source."
      },
      {
        category: "feature",
        description: "Task system integration across all CM panels",
        details: "Every panel has action buttons that open TaskModal pre-filled with patient data: Transitions → Follow-up task, Unmanaged Risk → Care Plan task, ER → Intervention task, Outreach → Patient Recall task, Polypharmacy → Med Reconciliation task. Reuses existing useTasks hook and localStorage persistence."
      },
      {
        category: "feature",
        description: "Contextual help icons for all 7 CM panels",
        details: "InfoButton with detailed help content (cmHelpContent.js) explaining each panel's purpose, columns, color coding, and available actions."
      },
    ],
  },
  {
    version: "1.16.1",
    date: "2026-02-17",
    title: "Clinical Dashboard — CTA Connections + Info Icons + Polish",
    type: "patch",
    changes: [
      {
        category: "feature",
        description: "Contextual help info icons on all dashboard panels",
        details: "InfoButton + InfoModal components added to all 6 panels and LocationSelector. Each info icon opens a modal explaining how the panel works, what each column means, and how to interpret colors and flags."
      },
      {
        category: "feature",
        description: "CTA buttons connecting all panels to the Task system",
        details: "Panel 2 (Alerts): Follow Up button on each patient card opens TaskModal pre-filled. Panel 3 (Worklist): Task icon per patient row. Panel 4 Tab Care Gaps: + Create Task button. Panel 4 Tab No Visit: Start Recall Campaign button. All CTAs pre-fill TaskModal with relevant category and patient data."
      },
      {
        category: "fix",
        description: "Fixed PanelTasks loading state that was hardcoded to false",
        details: "PanelTasks now receives actual loading state from useClinicalDashboard hook, allowing skeleton loaders to display during data loading."
      },
      {
        category: "improvement",
        description: "Task completion animation with fade effect",
        details: "Completing a task now shows a smooth 400ms animation: checkbox marks, text gets strike-through, row fades to 40% opacity and scales down slightly before updating state."
      }
    ]
  },
  {
    version: "1.16.0",
    date: "2026-02-17",
    title: "Clinical Dashboard — Full Refactor with 6 Panels + Task System",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "New Clinical Dashboard with 11-location selector and real clinical data",
        details: "Replaces mock PCP Dashboard. LocationSelector loads 11 locations from JSON data with persistence in localStorage. DashboardSubheader shows source date, patient count, and provider count."
      },
      {
        category: "feature",
        description: "Panel 1: KPI Overview with 6 segment cards + 2 composite cards",
        details: "Clickable KPI cards (Assigned, High Cost, High Risk, Emergency, Discharged, Admitted) navigate to PatientMap. Composite cards for Care Gaps and Coding Opportunities with progress bars and sub-metrics."
      },
      {
        category: "feature",
        description: "Panel 2: Real-Time Alerts (Admitted/Discharged/Emergency)",
        details: "3-column layout with scrollable alert cards. Each card shows patient name, facility, admit date, days since admission, LACE score badge (color-coded), behavioral health flag, diagnoses, PCP. Admitted column has pulse animation."
      },
      {
        category: "feature",
        description: "Panel 3: My Patients Worklist with clinical triage sort",
        details: "Filterable patient table (search, health status, visit timeframe, provider). 10 columns with inline flags (inpatient, discharged, high risk, polypharmacy, trigger, new member). Triage sort: admitted first, then discharged, then severity, then visit recency, then gaps. Row border-left color for Complex/End of Life/At Risk. 50 rows per page."
      },
      {
        category: "feature",
        description: "Panel 4: Insights with 3 tabs (Care Gaps, Coding, No Visit)",
        details: "Care Gaps tab: SVG donut ring, compliance bars per measure (color-coded by threshold). Coding tab: condition type chips, top HCCs list. No Visit tab: distribution mini-cards, percentage stat, contactability info. Fade transitions between tabs."
      },
      {
        category: "feature",
        description: "Panel 5: Patient Recall with distribution stats and contact table",
        details: "4 mini-stat cards (12+Mo, 9Mo, 6Mo, with phone). Recall table with patient name, since chip, status, gaps, clickable phone (tel:) and email (mailto:) links, provider, and Create Task action."
      },
      {
        category: "feature",
        description: "Panel 6: Task Management System with localStorage persistence",
        details: "CRUD task system (create, complete, cancel, delete) with 9 categories, 3 priorities, 4 statuses. TaskStats counters (Due Today, Overdue, Done Today). Grouped task list (Overdue, Due Today, Upcoming, Completed). Task items with checkbox, priority dot, category chip, due date badge."
      },
      {
        category: "feature",
        description: "TaskModal for creating/editing tasks with pre-fill from all panels",
        details: "Modal with react-hook-form: title, description, category, priority, due date, patient autocomplete. Pre-fills from Recall table Create Task CTA. Patient search filters by name or CIP ID with 2+ char threshold."
      },
      {
        category: "improvement",
        description: "Responsive layout for all clinical dashboard panels",
        details: "Full 6-panel grid at 1200px+. Panels 3+4 and 5+6 in 65%/35% side-by-side layout on large screens, stacking vertically on smaller. KPIs grid: 6 cols on md+, 3 on sm, 2 on xs."
      }
    ]
  },
  {
    version: "1.15.0",
    date: "2026-02-16",
    title: "Administration Module — Sidebar layout with Master Data & Roles",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "New Administration module with sidebar navigation",
        details: "Replaces old Roles & Privileges module. Sidebar with 2 sections: Master Data (Employees, Locations, PCP Configuration) and Roles & Permissions. Consumes real backend APIs via masterDataService + userManagementService."
      },
      {
        category: "feature",
        description: "Employees section with integrated user account creation",
        details: "DataGrid with search, filters (status, location, job position), admission date column. 'User Account' column shows 'Create User' button for employees without account. Detail dialog includes: general info, user account management (deactivate, unlock, reset password, force logout), assigned locations (add/remove), and PCP config panel for PCP employees."
      },
      {
        category: "feature",
        description: "Locations section with full CRUD and status management",
        details: "DataGrid with search and status filter. Create/edit form with manager dropdown, address, coordinates, clinical flag. Shows employee count and center key per location."
      },
      {
        category: "feature",
        description: "PCP Configuration section for managing PCP assignments",
        details: "Standalone section to register, edit, deactivate, and reactivate PCP configurations. DataGrid shows capacity, patient count, usage %, age range, acceptance flags, days/week. Filter by location and status."
      },
      {
        category: "feature",
        description: "Roles & Permissions with dual view: by user and by role",
        details: "Roles by User: autocomplete user selector, view/assign/revoke roles, force logout. Permissions by Role: select role, view CRUD permissions matrix across all system modules (read-only)."
      },
      {
        category: "refactor",
        description: "Removed old Roles & Privileges module with mock data",
        details: "Deleted src/modules/roles-privileges/ (18 files) which used secureStorage mock data. 3 placeholder functions added for SPs not yet built (getJobPositions, getUsers, getRoles) returning static data until backend implements the endpoints."
      },
    ],
  },
  {
    version: "1.14.0",
    date: "2026-02-16",
    title: "MasterData Service + Real Login Flow with Role Selection",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "MasterData service with 19 API endpoint functions",
        details: "New masterDataService.js covering all MasterData controller endpoints: employee info, locations CRUD, employees CRUD, employee-location assignments, PCP config management, role-modules, and statuses catalog. Full JSDoc with request/response examples."
      },
      {
        category: "feature",
        description: "Real post-login flow replacing hardcoded data",
        details: "AuthContext.signIn now calls getEmployeeInfo, getEmployeeLocations, and getRoleModules after login. User data (fullName, roles, locations, modules) is populated from backend instead of hardcoded values."
      },
      {
        category: "feature",
        description: "Multi-role selection dialog after login",
        details: "Users with 2+ roles see a RoleSelectorDialog to pick their active role. Single-role users are auto-selected. switchRole function allows changing roles during the session."
      },
      {
        category: "refactor",
        description: "AuthContext userData structure updated with roles, locations, and modules",
        details: "New userData includes: roles array, activeRole, locations with primary flag, modules with CRUD permissions, and legacy compatibility fields (rol, location, employee)."
      },
    ],
  },
  {
    version: "1.13.0",
    date: "2026-02-13",
    title: "Care Plan Wizard Dialog, Food Traffic Light, UI Mejorada & Print",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Wizard dialog with mode selection for AI Care Plan generation",
        details: "New dialog flow lets users choose between 'Care Plan Only' or 'Care Plan & Meal Plan'. The meal plan option uses a 2-step wizard (Step 1: Goals & Habits, Step 2: Dietary & Cultural preferences). Back navigation returns to mode selection"
      },
      {
        category: "feature",
        description: "Food Traffic Light classification in meal plan",
        details: "New accordion section classifies foods into 4 color-coded levels: green (eat freely), yellow (moderate), orange (preferably avoid), red (must avoid completely). Each food chip shows a tooltip with the reason. AI prompt updated to generate foodTrafficLight data"
      },
      {
        category: "improvement",
        description: "Enhanced meal plan UI with colors and icons",
        details: "Gradient header on meal plan card, colored nutrition target cards (amber/pink/indigo/teal), meal-type colored cards (breakfast amber, lunch green, dinner blue) with FreeBreakfast/LunchDining/DinnerDining icons, styled hydration box with LocalDrink icon"
      },
      {
        category: "feature",
        description: "Print buttons for care plan and meal plan",
        details: "New print icon buttons on care plan and meal plan card headers. Opens a clean print window with patient info header, section content, and basic print CSS"
      }
    ]
  },
  {
    version: "1.12.0",
    date: "2026-02-13",
    title: "AI Meal Plan Integration in Care Plans",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "AI-Powered personalized meal plan generation",
        details: "New mealPlanAiService.js generates a 7-day culturally appropriate meal plan using OpenAI, based on care plan results, allergies, dietary preferences and cultural food traditions. Includes nutrition targets, weekly plan (3 meals/day), foods to avoid, allergy-safe alternatives, snack suggestions and hydration guidelines"
      },
      {
        category: "feature",
        description: "Dietary Preferences and Cultural Diet fields in AI dialog",
        details: "Two new input fields in the Care Plan AI dialog: Dietary Preferences/Restrictions (low-sodium, vegetarian, diabetic, etc.) and Cultural/Regional Diet (Colombian, Haitian, Mexican, etc.) to personalize the meal plan"
      },
      {
        category: "improvement",
        description: "Removed manual Care Plan creation button",
        details: "Only the AI-powered generation button remains. Button text updated to 'Generate Care Plan & Meal Plan with AI'. Meal plan generates automatically in background after care plan completes"
      }
    ]
  },
  {
    version: "1.11.0",
    date: "2026-02-06",
    title: "Care Plans - AI-Powered Care Plan Generation + AdvancedMD Integration",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "AI-Powered Care Plan generation system",
        details: "New AI service (carePlanAiService.js) generates comprehensive structured care plans using OpenAI from clinical notes, allergies, physician goals and patient habits. Returns identified problems with ICD-10, goals, interventions, allergy safety notes, preventive recommendations, lifestyle modifications, follow-up plan and risk assessment"
      },
      {
        category: "feature",
        description: "AdvancedMD patient match in Care Plans page",
        details: "When selecting a patient in /Careplan, the system automatically matches against AdvancedMD, fetches clinical notes (Annual Wellness, Follow Up Exam, Telehealth, Hospital Discharge) and allergies. Displays match status chip and Chart ID"
      },
      {
        category: "feature",
        description: "Clinical Notes and Allergies cards in Care Plans page",
        details: "New Allergies (AdvancedMD) card with table (Allergy, Reaction, Treatment, Status) and Clinical Notes (AdvancedMD) card with table (Date, Type, Template, Chief Complaint, Assessment) displayed when AdvancedMD match succeeds"
      },
      {
        category: "feature",
        description: "AI Care Plan creation dialog with full result visualization",
        details: "Create Care Plan with AI button opens dialog to input goals and patient habits. AI result displayed with accordion sections: Risk Assessment, Identified Problems, Goals, Interventions, Allergy Safety Notes, Preventive Recommendations, Lifestyle Modifications and Follow Up Plan"
      },
      {
        category: "improvement",
        description: "Removed Introduction step from Care Plan creation wizard",
        details: "The /create-care-plan/:id route now goes directly to Diagnoses/Allergies/Habits step, skipping the Introduction screen"
      }
    ]
  },
  {
    version: "1.10.0",
    date: "2026-02-06",
    title: "Clinical Info - Rutas Individuales por Sub-Modulo",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "5 rutas individuales para sub-modulos de Clinical Info",
        details: "Nuevas paginas standalone para Discharged, Admitted, Emergency, High Cost y High Risk accesibles desde el sidebar. Cada pagina wrappea solo su provider, optimizando API calls"
      },
      {
        category: "improvement",
        description: "Sidebar actualizado con 5 items individuales",
        details: "La seccion Care Management ahora muestra Discharged, Admitted, Emergency, High Cost y High Risk como items separados con iconos y colores propios, reemplazando el item unico Clinical Info"
      },
      {
        category: "improvement",
        description: "Soporte i18n para nuevos items de navegacion",
        details: "Claves nav.discharged, nav.admitted, nav.emergency, nav.highCost, nav.highRisk agregadas en los 3 idiomas (en, es, cr)"
      },
      {
        category: "docs",
        description: "Documentacion de sub-modulos de Clinical Info",
        details: "5 archivos .md en src/modules/clinical-info/docs/ describiendo que existe y que falta en cada sub-modulo (tablas, columnas, filtros, charts)"
      },
      {
        category: "refactor",
        description: "AppRouter usa routes map para clinical-info",
        details: "La ruta /ClinicalInfo y las 5 nuevas rutas se manejan con clinicalInfoRoutes.map(), siguiendo el patron de mainRoutes y carePlansRoutes"
      }
    ]
  },
  {
    version: "1.9.0",
    date: "2026-02-06",
    title: "User Profile + Patient Profiles Refactor",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Pagina de perfil del usuario logueado",
        details: "Nueva pagina /profile muestra datos del usuario (nombre, rol, ubicacion, email, employee ID) con boton para cambiar contrasena. Link 'My Profile' en UserMenu ahora muestra info del usuario en vez del wizard de pacientes"
      },
      {
        category: "refactor",
        description: "Patient Profiles movido al sidebar Care Management",
        details: "El wizard de evaluacion de pacientes ahora esta disponible en /patient-profiles con acceso directo desde el sidebar seccion Care Management. Modulo wrapper en modules/care-management-system/patient-profiles/"
      },
      {
        category: "improvement",
        description: "Soporte i18n para User Profile y Patient Profiles",
        details: "Claves userProfile.* y nav.patientProfiles agregadas en los 3 idiomas (en, es, cr)"
      }
    ]
  },
  {
    version: "1.8.0",
    date: "2026-02-06",
    title: "Layout Redesign - Modern SaaS Healthcare Shell",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Sidebar oscuro con navegacion por secciones RBAC",
        details: "Nuevo sidebar con tema oscuro (#071E24), navegacion organizada en 3 secciones (Clinical, Care Management, Administration) con filtrado por permisos. Modo colapsado/expandido persistente en localStorage"
      },
      {
        category: "feature",
        description: "View as Role para administradores",
        details: "Los administradores pueden simular la vista de cualquier rol del sistema desde el menu de usuario. Solo afecta visibilidad del sidebar, no bloquea rutas"
      },
      {
        category: "feature",
        description: "Menu de usuario enriquecido con avatar",
        details: "Avatar circular como trigger, popover con info del usuario (nombre, rol, ubicacion), acceso a perfil, selector de rol (admin), y logout con estilo"
      },
      {
        category: "improvement",
        description: "AppBar simplificado a 2 tabs",
        details: "Solo Dashboard y Patient Map en la barra superior. La navegacion de modulos se movio al sidebar con secciones colapsables"
      },
      {
        category: "fix",
        description: "Corregido bug de ancho dinamico del contenido",
        details: "El ancho del area de contenido principal ahora respeta el estado colapsado/expandido del sidebar en vez de estar hardcodeado a 240px"
      },
      {
        category: "refactor",
        description: "Configuracion de navegacion centralizada",
        details: "Nuevo archivo navigationConfig.js como fuente unica de verdad para rutas, permisos, iconos e i18n keys del sidebar y AppBar"
      },
      {
        category: "improvement",
        description: "Soporte i18n completo para navegacion",
        details: "Claves nav.* agregadas en los 3 idiomas (en, es, cr) para todos los items de navegacion"
      },
      {
        category: "improvement",
        description: "Roles del sistema expandidos a 10",
        details: "UserRoleName en Enum.js ahora incluye los 10 roles: CEO, Admin, Physician, Nurse, Care Manager, Care Coordinator, Billing, Coder, Reception, Support"
      }
    ]
  },
  {
    version: "1.7.0",
    date: "2026-02-05",
    title: "AI Specific Conditions - Refactor Self-Contained",
    type: "minor",
    changes: [
      {
        category: "improvement",
        description: "Refactor de Specific Conditions (ClinicalAiPanels) a componente self-contained",
        details: "El panel ahora obtiene datos directamente de AdvancedMD Context y Redux en vez de depender de datos pre-procesados del padre. Usa buildHedisContextText para contexto clínico completo (demographics, notas, meds, labs, allergies)"
      },
      {
        category: "improvement",
        description: "Botón Generate manual en vez de auto-trigger",
        details: "El análisis de condiciones ya no se ejecuta automáticamente al montar el componente. El usuario debe presionar 'Generate Condition Analysis with AI' para iniciar, evitando API calls innecesarias"
      },
      {
        category: "improvement",
        description: "Prompt de IA mejorado con separación system/user",
        details: "El prompt ahora usa mensajes system y user separados, temperatura reducida a 0.15, y enfatiza que solo use diagnósticos explícitamente documentados en los datos clínicos"
      },
      {
        category: "refactor",
        description: "Eliminado hook useClinicalAiSections y limpieza de props",
        details: "Se eliminó el hook intermediario y se simplificaron los props de AiReportsPanel y PatientMapPage removiendo clinicalNotesForAiPanels"
      }
    ]
  },
  {
    version: "1.6.0",
    date: "2026-02-02",
    title: "Cambio de Contraseña Obligatorio - Seguridad de Acceso",
    type: "minor",
    changes: [
      {
        category: "security",
        description: "Cambio obligatorio de contraseña por defecto",
        details: "Los usuarios que inician sesión con la contraseña por defecto (TestMCM2025) son redirigidos a una página de cambio de contraseña antes de poder acceder al sistema"
      },
      {
        category: "feature",
        description: "Nueva página de cambio de contraseña",
        details: "Página standalone /ChangePassword con la misma estética del Login, dos campos (contraseña actual y nueva) con eye toggle, validación de mínimo 8 caracteres y que sea diferente a la actual"
      },
      {
        category: "security",
        description: "Flujo seguro post-cambio de contraseña",
        details: "Después de cambiar la contraseña exitosamente, se cierra la sesión automáticamente y se redirige al Login para re-autenticarse con la nueva contraseña"
      }
    ]
  },
  {
    version: "1.5.0",
    date: "2026-01-31",
    title: "Roles & Privileges - Módulo de Administración RBAC",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Módulo completo de Roles & Privileges con 3 tabs",
        details: "Tab Users (CRUD usuarios, toggle activo/inactivo), Tab Roles (CRUD roles con checkboxes de permisos), Tab Module Access (matriz de acceso roles vs módulos)"
      },
      {
        category: "feature",
        description: "Gestión de usuarios con DataGrid",
        details: "Tabla de usuarios con búsqueda, creación, edición, y toggle de estado activo/inactivo con diálogo de confirmación"
      },
      {
        category: "feature",
        description: "Gestión de roles con permisos granulares",
        details: "36 permisos organizados por categoría (Clinical, Administrative, AI, Support) con checkboxes agrupados por módulo y Select All"
      },
      {
        category: "feature",
        description: "Matriz de acceso a módulos",
        details: "Tabla matrix de 15 módulos x 11 roles con niveles: No Access, Read Only, Read & Write, Full Access. Admin bloqueado en Full Access"
      },
      {
        category: "feature",
        description: "Capa de datos mock con secureStorage",
        details: "Datos seed (11 roles, 36 permisos, 15 módulos, 8 usuarios) en secureStorage encriptado. Servicios preparados para swap a API real"
      }
    ]
  },
  {
    version: "1.4.1",
    date: "2026-01-28",
    title: "Fix - Usuario en Sidebar con secureStorage",
    type: "patch",
    changes: [
      {
        category: "fix",
        description: "Corregido usuario undefined en Sidebar",
        details: "SidebarComponent ahora usa useAuthUser hook para leer usuario encriptado en lugar de localStorage directo"
      }
    ]
  },
  {
    version: "1.4.0",
    date: "2026-01-28",
    title: "Coding Opportunities - Integración de Comentarios Backend",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Integración de comentarios en Coding Opportunities",
        details: "Nuevo endpoint POST /api/CodingOpportunities/comment para guardar comentarios cuando el doctor modifica una decisión"
      },
      {
        category: "feature",
        description: "Integración de comentarios de paciente en Finish Process",
        details: "Nuevo endpoint POST /api/CodingOpportunities/patient-comment para guardar comentarios al finalizar el proceso"
      },
      {
        category: "improvement",
        description: "Funciones de comentarios agregadas al CodingOpContext",
        details: "Nuevas funciones addCodingOpComment y addPatientComment en el contexto para centralizar la lógica"
      },
      {
        category: "improvement",
        description: "Uso correcto de employeeId del usuario logueado",
        details: "Todos los endpoints de Coding Opportunities ahora usan user.employee para identificar al usuario"
      }
    ]
  },
  {
    version: "1.3.0",
    date: "2026-01-28",
    title: "Lab Phlebotomy - Integración Backend API",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Integración de endpoint POST para Lab Phlebotomy",
        details: "Nuevo servicio createLabPhlebotomy.js para guardar registros en base de datos en lugar de localStorage"
      },
      {
        category: "feature",
        description: "Tarjeta de información del paciente mejorada",
        details: "Nueva tarjeta visual que muestra CPID, Member ID, DOB, edad, género, teléfono, email y estado (ACTIVE/INACTIVE)"
      },
      {
        category: "improvement",
        description: "IDs numéricos para PCP Providers y Appointment Types",
        details: "Constantes actualizadas con IDs del backend para mapeo correcto con la API"
      },
      {
        category: "improvement",
        description: "Staff auto-asignado del usuario logueado",
        details: "El campo staff ahora se asigna automáticamente desde user.employee, eliminando selección manual"
      },
      {
        category: "refactor",
        description: "Simplificación del formulario de Lab Phlebotomy",
        details: "Eliminados campos redundantes (Member ID, DOB, Phone, Mobile) ya que la info se muestra en la tarjeta del paciente"
      },
      {
        category: "docs",
        description: "Documentación de migración Lab Phlebotomy",
        details: "Agregado MIGRATION_STATUS.md con estado actual y pendientes de la migración a backend"
      }
    ]
  },
  {
    version: "1.2.0",
    date: "2026-01-28",
    title: "Dashboards por Rol - Sistema Multi-Dashboard",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Dashboards específicos por rol de usuario",
        details: "MainPage ahora renderiza dashboards diferentes según el rol: CEO, Admin, PCP, Care Manager"
      },
      {
        category: "feature",
        description: "CEO Executive Dashboard",
        details: "Nuevo dashboard con KPIs poblacionales, métricas financieras, calidad HEDIS y tendencias"
      },
      {
        category: "feature",
        description: "Admin System Dashboard",
        details: "Dashboard de administración con estadísticas de usuarios, salud del sistema y logs de auditoría"
      },
      {
        category: "feature",
        description: "Care Manager Dashboard",
        details: "Dashboard de gestión de cuidados con worklist, outreach, referrals y tareas"
      },
      {
        category: "feature",
        description: "Dashboard Switcher para Admin",
        details: "Panel de preview que permite a administradores ver todos los dashboards disponibles"
      },
      {
        category: "feature",
        description: "Componentes compartidos reutilizables",
        details: "MetricCard, KPICard, ChartCard y DashboardHeader con diseño moderno y gradientes"
      },
      {
        category: "security",
        description: "Agregado rol CEO al sistema RBAC",
        details: "Nuevo rol CEO con permisos de lectura para métricas ejecutivas y reportes"
      },
      {
        category: "improvement",
        description: "Rediseño visual de dashboards",
        details: "Nuevo diseño con gradientes, sombras, iconos animados y efectos hover mejorados"
      },
      {
        category: "refactor",
        description: "PCP Dashboard extraído de MainPage",
        details: "Dashboard clínico ahora es un componente independiente con diseño mejorado"
      }
    ]
  },
  {
    version: "1.1.0",
    date: "2026-01-25",
    title: "HIPAA Compliance - Fase 2 y 3",
    type: "minor", // major, minor, patch
    changes: [
      {
        category: "security",
        description: "Implementado RBAC (Control de Acceso Basado en Roles)",
        details: "9 roles definidos con 30+ permisos granulares por módulo"
      },
      {
        category: "security",
        description: "Implementado Sistema de Auditoría",
        details: "Registro automático de acceso a datos de pacientes con 25+ tipos de eventos"
      },
      {
        category: "security",
        description: "Implementada Desensibilización de PHI para OpenAI",
        details: "Sanitización automática de datos sensibles antes de enviar a IA (Safe Harbor Method)"
      },
      {
        category: "feature",
        description: "Módulo de Soporte Técnico",
        details: "Panel de debugging con captura de logs y API calls, solo para usuarios autorizados"
      },
      {
        category: "feature",
        description: "Página de Acceso Denegado",
        details: "Nueva página /Unauthorized cuando el usuario no tiene permisos"
      }
    ]
  },
  {
    version: "1.0.0",
    date: "2026-01-24",
    title: "HIPAA Compliance - Fase 1",
    type: "major",
    changes: [
      {
        category: "security",
        description: "Removidas credenciales de API del código fuente",
        details: "Las claves de OpenAI ahora se cargan desde variables de entorno"
      },
      {
        category: "security",
        description: "Implementado Timeout de Sesión",
        details: "Auto-logout después de 15 minutos de inactividad con modal de advertencia"
      },
      {
        category: "security",
        description: "Implementado Almacenamiento Seguro",
        details: "Datos de usuario encriptados con AES-256 en sessionStorage"
      },
      {
        category: "security",
        description: "Configuración HTTPS preparada",
        details: "URLs HTTPS listas para activar cuando backend tenga SSL"
      },
      {
        category: "config",
        description: "Creado .env.example",
        details: "Template de variables de entorno para nuevos desarrolladores"
      }
    ]
  },
  {
    version: "0.9.0",
    date: "2026-01-23",
    title: "Pre-HIPAA Baseline",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Módulo de Coding Opportunities v3",
        details: "Nueva versión del worklist con análisis de IA"
      },
      {
        category: "feature",
        description: "Módulo de Care Gaps actualizado",
        details: "Mejoras en la interfaz y filtros"
      },
      {
        category: "feature",
        description: "Specialist Referrals Module",
        details: "Nuevo módulo self-contained para referidos"
      }
    ]
  }
];

/**
 * Obtener cambios por categoría
 */
export const getChangesByCategory = (version) => {
  const release = changelog.find(r => r.version === version);
  if (!release) return {};

  return release.changes.reduce((acc, change) => {
    if (!acc[change.category]) acc[change.category] = [];
    acc[change.category].push(change);
    return acc;
  }, {});
};

/**
 * Obtener última versión
 */
export const getLatestVersion = () => changelog[0];

/**
 * Categorías de cambios
 */
export const CHANGE_CATEGORIES = {
  security: { label: "Seguridad", color: "#f44336", icon: "🔒" },
  feature: { label: "Nueva Funcionalidad", color: "#4caf50", icon: "✨" },
  fix: { label: "Corrección", color: "#ff9800", icon: "🐛" },
  improvement: { label: "Mejora", color: "#2196f3", icon: "⚡" },
  config: { label: "Configuración", color: "#9c27b0", icon: "⚙️" },
  docs: { label: "Documentación", color: "#607d8b", icon: "📚" },
  refactor: { label: "Refactorización", color: "#795548", icon: "♻️" },
  deprecated: { label: "Deprecado", color: "#9e9e9e", icon: "⚠️" }
};

export default changelog;
